import {toast} from '@common-web/hooks/use-toast';
import {formatTRPCError} from '@common-web/lib/trpc';

const DEFAULT_TITLE = 'Something went wrong';

// Mirrors the generic fallback returned by formatTRPCError when no usable
// message can be extracted from the error. When we hit it, prefer a caller
// supplied fallbackMessage if one was provided.
const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

export interface ReportErrorOptions {
  /** Toast title. Defaults to "Something went wrong". */
  title?: string;
  /** Explicit description. When omitted, derived from the error via formatTRPCError. */
  description?: string;
  /** Used when the error yields no usable message. */
  fallbackMessage?: string;
  /** Toast variant. Defaults to "destructive". */
  variant?: 'destructive' | 'default';
  /** Skip the toast (intentional/already-surfaced swallow). Still logs. */
  silent?: boolean;
  /** Log to console.error. Defaults to true. */
  log?: boolean;
  /** Label for the console log, e.g. "RfcFileList.delete". */
  context?: string;
  /** Extra side effect, e.g. setStatus('failed'). Runs even when silent. */
  onError?: (error: unknown) => void;
}

function resolveDescription(error: unknown, options: ReportErrorOptions): string {
  if (options.description !== undefined) return options.description;
  const formatted = formatTRPCError(error);
  if (formatted === GENERIC_ERROR_MESSAGE && options.fallbackMessage) {
    return options.fallbackMessage;
  }
  return formatted;
}

/**
 * Central place to surface a caught error: logs it, runs an optional side
 * effect, and (unless silent) shows a destructive toast with a human-readable
 * message. Safe to call from anywhere — the toast store is module-global.
 */
export function reportError(error: unknown, options: ReportErrorOptions = {}): void {
  const {log = true, context, onError, silent = false} = options;

  if (log) {
    if (context) {
      console.error(`[${context}]`, error);
    } else {
      console.error(error);
    }
  }

  if (onError) {
    try {
      onError(error);
    } catch (callbackError) {
      console.error('[reportError] onError callback threw', callbackError);
    }
  }

  if (silent) return;

  toast({
    title: options.title ?? DEFAULT_TITLE,
    description: resolveDescription(error, options),
    variant: options.variant ?? 'destructive',
  });
}

/**
 * Composable wrapper that runs an async operation and surfaces any failure via
 * reportError, returning undefined on error. Removes try/catch boilerplate at
 * call sites.
 */
export async function withErrorToast<T>(
  fn: () => Promise<T>,
  options?: ReportErrorOptions,
): Promise<T | undefined> {
  try {
    return await fn();
  } catch (error) {
    reportError(error, options);
    return undefined;
  }
}
